import { useState, useEffect } from "react";
import axiosInstance from "../utils/axios";
import useAuth from "./useAuth";

/**
 * Hook to check if current user has permission to perform an action on a resource
 * in a specific association
 *
 * @param resource - The resource name (e.g., "invoice", "meeting", "user")
 * @param action - The action to perform (e.g., "create", "read", "update", "delete")
 * @param associationId - The association context
 * @returns Object with { hasPermission: boolean, loading: boolean, error: string | null }
 */
export const usePermission = (
  resource: string,
  action: string,
  associationId: number | undefined
): { hasPermission: boolean; loading: boolean; error: string | null } => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get current user from Auth Context
  const { currentUser } = useAuth();

  useEffect(() => {
    const checkPermission = async () => {
      // If no user or no associationId, no permission
      if (!currentUser?.id || !associationId) {
        setHasPermission(false);
        setLoading(false);
        return;
      }

      // SuperAdmin always has permission
      if (currentUser.isSuperAdmin) {
        setHasPermission(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch effective permissions for user in this association
        const response = await axiosInstance.get(
          `/associations/${associationId}/members/${currentUser.id}/permissions`
        );

        const permissions = response.data as Record<string, string[]>;

        // Check if user has the required permission
        const resourceActions = permissions[resource] || [];
        const allowed = resourceActions.includes(action);

        setHasPermission(allowed);
      } catch (err: any) {
        console.error("Error checking permission:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Permission check failed"
        );
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [
    resource,
    action,
    associationId,
    currentUser?.id,
    currentUser?.isSuperAdmin,
  ]);

  return { hasPermission, loading, error };
};

/**
 * Hook to get all effective permissions for current user in an association
 *
 * @param associationId - The association context
 * @returns Object with { permissions: Record<string, string[]>, loading: boolean, error: string | null }
 */
export const useEffectivePermissions = (
  associationId: number | undefined
): {
  permissions: Record<string, string[]>;
  loading: boolean;
  error: string | null;
} => {
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get current user from Auth Context
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!currentUser?.id || !associationId) {
        setPermissions({});
        setLoading(false);
        return;
      }

      // SuperAdmin has all permissions
      if (currentUser.isSuperAdmin) {
        setPermissions({
          invoice: ["create", "read", "update", "delete"],
          meeting: ["create", "read", "update", "delete"],
          user: ["create", "read", "update", "delete"],
          association: ["read", "update"],
          unit: ["create", "read", "update", "delete"],
          property: ["create", "read", "update", "delete"],
          road: ["create", "read", "update", "delete"],
          file: ["create", "read", "delete"],
          voucher: ["create", "read", "update", "delete"],
          account: ["create", "read", "update", "delete"],
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await axiosInstance.get(
          `/associations/${associationId}/members/${currentUser.id}/permissions`
        );

        setPermissions(response.data);
      } catch (err: any) {
        console.error("Error fetching permissions:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch permissions"
        );
        setPermissions({});
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [associationId, currentUser?.id, currentUser?.isSuperAdmin]);

  return { permissions, loading, error };
};

/**
 * Hook to get all effective permissions for a specific user in an association
 * Can be used to view another user's permissions (not just current user)
 *
 * @param associationId - The association context
 * @param userId - The user ID to fetch permissions for
 * @param refreshKey - Optional key to trigger refetch (increment to refresh)
 * @returns Object with { permissions: Record<string, string[]>, loading: boolean, error: string | null }
 */
export const useUserEffectivePermissions = (
  associationId: number | undefined,
  userId: number | undefined,
  refreshKey?: number
): {
  permissions: Record<string, string[]>;
  loading: boolean;
  error: string | null;
} => {
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!userId || !associationId) {
        setPermissions({});
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await axiosInstance.get(
          `/associations/${associationId}/members/${userId}/permissions`
        );

        setPermissions(response.data);
      } catch (err: any) {
        console.error("Error fetching user permissions:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch permissions"
        );
        setPermissions({});
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [associationId, userId, refreshKey]);

  return { permissions, loading, error };
};

/**
 * Hook to preview permissions for given roles in an association
 * Used by ADMIN users to see what permissions a user would have with selected roles
 *
 * @param associationId - The association context
 * @param roles - Array of role names to preview
 * @returns Object with { permissions: Record<string, string[]>, loading: boolean, error: string | null }
 */
export const usePreviewPermissions = (
  associationId: number | undefined,
  roles: string[]
): {
  permissions: Record<string, string[]>;
  loading: boolean;
  error: string | null;
} => {
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPreviewPermissions = async () => {
      if (!associationId || roles.length === 0) {
        setPermissions({});
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const rolesParam = roles.join(",");
        const response = await axiosInstance.get(
          `/associations/${associationId}/permissions/preview?roles=${rolesParam}`
        );

        setPermissions(response.data);
      } catch (err: any) {
        console.error("Error fetching preview permissions:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch preview permissions"
        );
        setPermissions({});
      } finally {
        setLoading(false);
      }
    };

    fetchPreviewPermissions();
  }, [associationId, roles.join(",")]);

  return { permissions, loading, error };
};

/**
 * Synchronous permission check (doesn't make API calls)
 * Use this for quick checks if you already have permissions loaded
 *
 * @param permissions - The permissions object (from useEffectivePermissions)
 * @param resource - The resource name
 * @param action - The action to perform
 * @returns boolean
 */
export const checkPermission = (
  permissions: Record<string, string[]>,
  resource: string,
  action: string
): boolean => {
  const resourceActions = permissions[resource] || [];
  return resourceActions.includes(action);
};

/**
 * Custom permission type - matches backend PermissionType enum
 */
export type PermissionType = "grant" | "deny";

/**
 * Custom permission interface
 */
export interface CustomPermission {
  id: number;
  resource: string;
  actions: string[];
  type: PermissionType;
}

/**
 * Hook to get custom permissions for a user in an association
 * Only SuperAdmin can view/edit custom permissions
 *
 * @param associationId - The association context
 * @param userId - The user ID to fetch custom permissions for
 * @param refreshKey - Optional key to trigger refetch
 * @returns Object with custom permissions, loading state, and error
 */
export const useCustomPermissions = (
  associationId: number | undefined,
  userId: number | undefined,
  refreshKey?: number
): {
  customPermissions: CustomPermission[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const [customPermissions, setCustomPermissions] = useState<
    CustomPermission[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refetch = () => setInternalRefreshKey((k) => k + 1);

  useEffect(() => {
    const fetchCustomPermissions = async () => {
      if (!userId || !associationId) {
        setCustomPermissions([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await axiosInstance.get(
          `/associations/${associationId}/members/${userId}/permissions/custom`
        );

        setCustomPermissions(response.data);
      } catch (err: any) {
        console.error("Error fetching custom permissions:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch custom permissions"
        );
        setCustomPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomPermissions();
  }, [associationId, userId, refreshKey, internalRefreshKey]);

  return { customPermissions, loading, error, refetch };
};

/**
 * Add a custom permission override for a user
 *
 * @param associationId - The association context
 * @param userId - The user ID
 * @param resource - The resource name
 * @param actions - Array of actions
 * @param type - "grant" or "deny"
 */
export const addCustomPermission = async (
  associationId: number,
  userId: number,
  resource: string,
  actions: string[],
  type: PermissionType
): Promise<CustomPermission> => {
  const response = await axiosInstance.post(
    `/associations/${associationId}/members/${userId}/permissions`,
    { resource, actions, type }
  );
  return response.data;
};

/**
 * Remove a custom permission override
 *
 * @param associationId - The association context
 * @param userId - The user ID
 * @param permissionId - The custom permission ID to remove
 */
export const removeCustomPermission = async (
  associationId: number,
  userId: number,
  permissionId: number
): Promise<void> => {
  await axiosInstance.delete(
    `/associations/${associationId}/members/${userId}/permissions/${permissionId}`
  );
};
